import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Result, Button, Typography, Card, Space } from 'antd';
import { CheckCircleOutlined, HomeOutlined, PhoneOutlined, LoginOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { confirmCheckoutSession } from '../../services/backendService';
import AuthModal from '../../components/auth/AuthModal';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const { currentUser } = useAuth();
  
  // Get payment details from URL parameters
  const paymentIntentId = searchParams.get('payment_intent');
  const sessionId = searchParams.get('session_id');
  const clientReferenceId = searchParams.get('client_reference_id');
  const amount = searchParams.get('amount_total');

  useEffect(() => {
    const run = async () => {
      try {
        if (sessionId) {
          await confirmCheckoutSession(sessionId);
        }
      } catch (e) {
        console.error('Confirm session failed', e);
      }
    };
    run();
  }, [sessionId]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleCallNow = () => {
    window.open('tel:+18885046944', '_self');
  };

  const handleOpenAuth = () => setIsAuthOpen(true);
  const handleCloseAuth = () => setIsAuthOpen(false);

  return (
    <>
      <Helmet>
        <title>Payment Successful - HSP</title>
        <meta name="description" content="Your payment has been processed successfully. We'll be in touch soon to schedule your services." />
      </Helmet>

      <div style={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: theme.fonts.primary
      }}>
        <Card
          style={{
            maxWidth: '600px',
            width: '100%',
            borderRadius: '20px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            border: 'none'
          }}
        >
          <Result
            icon={<CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '80px' }} />}
            title={
              <Title level={2} style={{ color: theme.colors.textPrimary, fontFamily: theme.fonts.primary }}>
                Payment Successful!
              </Title>
            }
            subTitle={
              <div style={{ textAlign: 'center' }}>
                <Paragraph style={{ fontSize: '18px', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                  Thank you for your payment! Your service request has been confirmed.
                </Paragraph>
                <Paragraph style={{ fontSize: '16px', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                  We'll be in touch within 24 hours to schedule your services.
                </Paragraph>
                {clientReferenceId && (
                  <Text style={{ fontSize: '14px', color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                    Booking ID: {clientReferenceId}
                  </Text>
                )}
              </div>
            }
            extra={
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ color: theme.colors.textPrimary, fontFamily: theme.fonts.primary }}>
                    What's Next?
                  </Title>
                  <Paragraph style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                    1. Our team will review your service request<br/>
                    2. We'll contact you to confirm your preferred date and time<br/>
                    3. Our professionals will arrive at your property to complete the services
                  </Paragraph>
                  <Paragraph style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
                    Log in to track your booking status and see updates in your dashboard.
                  </Paragraph>
                </div>
                
                <Space size="large" style={{ justifyContent: 'center', width: '100%' }}>
                  {currentUser ? (
                    <Button
                      type="primary"
                      size="large"
                      icon={<HomeOutlined />}
                      onClick={() => navigate('/dashboard')}
                      style={{
                        background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                        border: 'none',
                        borderRadius: '16px',
                        height: '50px',
                        padding: '0 30px',
                        fontFamily: theme.fonts.primary,
                        fontWeight: '700',
                        fontSize: '16px',
                        boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                        transition: 'all 0.3s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Dashboard
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      size="large"
                      icon={<LoginOutlined />}
                      onClick={handleOpenAuth}
                      style={{
                        background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                        border: 'none',
                        borderRadius: '16px',
                        height: '50px',
                        padding: '0 30px',
                        fontFamily: theme.fonts.primary,
                        fontWeight: '700',
                        fontSize: '16px',
                        boxShadow: '0 8px 25px rgba(39, 174, 96, 0.4)',
                        transition: 'all 0.3s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Login
                    </Button>
                  )}
                  
                  <Button
                    size="large"
                    icon={<PhoneOutlined />}
                    onClick={handleCallNow}
                    style={{
                      background: 'transparent',
                      color: theme.colors.primary,
                      border: `2px solid ${theme.colors.primary}`,
                      borderRadius: '16px',
                      height: '50px',
                      padding: '0 30px',
                      fontFamily: theme.fonts.primary,
                      fontWeight: '700',
                      fontSize: '16px',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Call Us: (888) 504-6944
                  </Button>
                </Space>
              </Space>
            }
          />
        </Card>
        <AuthModal visible={isAuthOpen} onClose={handleCloseAuth} />
      </div>
    </>
  );
};

export default PaymentSuccessPage;
